import React, { useState, useEffect, useMemo, useCallback } from "react";
import Avatar from "../../components/Avatar";
import { Trash2, Plus, X, Search, Edit2, Lock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import {
  useGetAcademyQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useGetDepartmentQuery,
  useGetUsersQuery,
  useGetRolesQuery,
  useSoftDeleteUserMutation,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetDivisionsQuery,
  useCreateDivisionMutation,
  useUpdateDivisionMutation,
  useDeleteDivisionMutation,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "../../slices/userApiSlice";
import { toast } from "sonner";
import { isValidEmail } from "../../utils/userManagementUtils";
import ResetPasswordModal from "../../components/UserManagement/ResetPasswordModal";
import { useAdminResetPasswordMutation } from "../../slices/userApiSlice";

// Constants
const TABS = [
  "Manager",
  "Supervisor",
  "Technician",
  "CFM",
  "Department",
  "Division",
  "Role",
];
const ROWS_PER_PAGE = 10;
const TEXT_TRUNCATE_LENGTH = 20;
const CFM_ROLE_NAMES = ["cfm", "chief facility manager"];

const AdminUser = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // UI State
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Manager");

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      // Find matching tab case-insensitively
      const matchingTab = TABS.find(t => t.toLowerCase() === tabParam.toLowerCase());
      if (matchingTab) {
        setActiveTab(matchingTab);
      }
    }
  }, [searchParams]);

  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showModalDepartment, setShowModalDepartment] = useState(false);
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [deleteRoleConfirm, setDeleteRoleConfirm] = useState({
    open: false,
    id: null,
  });
  const [deleteUserConfirm, setDeleteUserConfirm] = useState({
    open: false,
    id: null,
  });

  // User Form State
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDiv, setSelectedDiv] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [emailError, setEmailError] = useState("");

  // Department Form State
  const [departmentName, setDepartmentName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editDepartmentId, setEditDepartmentId] = useState(null);
  const [formError, setFormError] = useState(null);

  // Division Form State
  const [showModalDivision, setShowModalDivision] = useState(false);
  const [divisionName, setDivisionName] = useState("");
  const [divisionDescription, setDivisionDescription] = useState("");
  const [isEditingDivision, setIsEditingDivision] = useState(false);
  const [editDivisionId, setEditDivisionId] = useState(null);
  const [selectedDivisionDept, setSelectedDivisionDept] = useState(null);

  // Role Management State
  const [roleNameInput, setRoleNameInput] = useState("");
  const [roleDescriptionInput, setRoleDescriptionInput] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);
  const [editRoleId, setEditRoleId] = useState(null);

  // Data State
  const [data, setData] = useState([]);
  const [sortOrder, setSortOrder] = useState({ column: null, ascending: true });

  // Password Reset State
  const [showResetModal, setShowResetModal] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [adminResetPassword, { isLoading: isResettingPassword }] =
    useAdminResetPasswordMutation();

  // API Queries
  const { data: academies, isLoading: isLoadingAcademies } =
    useGetAcademyQuery();
  const {
    data: department,
    refetch: refetchDepartments,
    isLoading: isLoadingDepartments,
  } = useGetDepartmentQuery();
  const {
    data: users,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useGetUsersQuery();
  const { data: roles, isLoading: isLoadingRoles } = useGetRolesQuery();
  const {
    data: divisions,
    isLoading: isLoadingDivisions,
    refetch: refetchDivisions,
  } = useGetDivisionsQuery();

  // API Mutations
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [createDepartment] = useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: updating }] =
    useUpdateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();
  const [createDivision] = useCreateDivisionMutation();
  const [updateDivision, { isLoading: isUpdatingDivision }] =
    useUpdateDivisionMutation();
  const [deleteDivision] = useDeleteDivisionMutation();
  const [softDeleteUser] = useSoftDeleteUserMutation();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();

  // Helper functions
  const matchesAcademyFilter = useCallback((item, academyId) => {
    if (!academyId || academyId === "all") return true;
    return item.academyId === academyId || item._id === academyId;
  }, []);

  const matchesSearchTerm = useCallback((item, searchTerm) => {
    if (!item) return false;
    const lowerSearch = searchTerm.toLowerCase();
    return Object.values(item).some((v) =>
      v ? String(v).toLowerCase().includes(lowerSearch) : false,
    );
  }, []);

  const isCFMRole = useCallback((roleName) => {
    if (!roleName) return false;
    return CFM_ROLE_NAMES.includes(roleName.toLowerCase());
  }, []);

  const getRoleIdByTab = useCallback(
    (tab) => {
      if (!roles || !Array.isArray(roles)) return null;
      const searchName = tab.toLowerCase();
      const role = roles.find((r) => {
        const name = r.name?.toLowerCase();
        if (searchName === "cfm")
          return name === "cfm" || name === "chief facility manager";
        return name === searchName;
      });
      // Extract ID as string, handling both roleId and _id
      const id = role?.roleId || role?._id;
      return id ? (typeof id === 'string' ? id : id.$oid || id.toString()) : null;
    },
    [roles],
  );

  // Effects
  useEffect(() => {
    if (users) setData(users);
  }, [users]);

  // Clear department and division when CFM or admin tab is active
  useEffect(() => {
    if (activeTab === "CFM" || activeTab === "admin") {
      setSelectedDept(null);
      setSelectedDiv(null);
    }
  }, [activeTab]);

  const handleCreateUser = async () => {
    if (!name || !email || !password || !employeeId || !selectedAcademy) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Invalid email format");
      return;
    }

    // Role-specific validation (case-insensitive check)
    const activeTabLower = activeTab.toLowerCase();

    if (activeTabLower === "manager" && !selectedDept?.value) {
      toast.error("Department is required for Manager role");
      return;
    }

    if (
      (activeTabLower === "supervisor" || activeTabLower === "technician") &&
      (!selectedDept?.value || !selectedDiv?.value)
    ) {
      toast.error("Department and Division are required for this role");
      return;
    }

    const roleId = getRoleIdByTab(activeTab);
    if (!roleId) {
      toast.error(`Role identifier not found for ${activeTab}`);
      return;
    }

    try {
      // Build user data conditionally based on role
      const userData = {
        name,
        employeeId,
        email,
        password,
        academyId: selectedAcademy.value,
        roleId: roleId,
      };

      // Only include departmentId for Manager, Supervisor, and Technician
      if (
        activeTabLower === "manager" ||
        activeTabLower === "supervisor" ||
        activeTabLower === "technician"
      ) {
        userData.departmentId = selectedDept?.value;
      }

      // Only include divisionId for Supervisor and Technician
      if (activeTabLower === "supervisor" || activeTabLower === "technician") {
        userData.divisionId = selectedDiv?.value;
      }

      await createUser(userData).unwrap();

      toast.success("User created successfully");
      resetForm();
      setShowModal(false);
      refetchUsers();
    } catch (err) {
      // Check for specific error messages and provide better context
      const errorMessage =
        err?.data?.message || err?.data || "Failed to create user";

      if (errorMessage.includes("CFM already exists")) {
        toast.error(
          "A CFM (Chief Facilities Manager) already exists for this academy. Please select a different academy or update the existing CFM.",
        );
      } else if (errorMessage.includes("Manager already exists")) {
        toast.error(
          "A Manager already exists for this academy. Each academy can have only one Manager across all departments.",
        );
      } else if (errorMessage.includes("duplicate")) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleUpdateUser = async () => {
    if (!name || !email || !employeeId || !selectedAcademy) {
      toast.error("Required fields missing");
      return;
    }

    try {
      await updateUser({
        id: editUserId,
        name,
        email,
        employeeId,
        roleId: selectedRole?.value,
        academyId: selectedAcademy?.value,
        departmentId: selectedDept?.value,
        divisionId: selectedDiv?.value,
      }).unwrap();

      toast.success("User updated successfully");
      setShowModal(false);
      setIsEditingUser(false);
      setEditUserId(null);
      resetForm();
      refetchUsers();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update user");
    }
  };

  const openEditUserModal = (user) => {
    setIsEditingUser(true);
    setEditUserId(user.userId);
    setName(user.name);
    setEmail(user.email);
    setEmployeeId(user.employeeId);
    setSelectedAcademy(
      academies?.find(
        (a) => a.academyId === user.academyId || a._id === user.academyId,
      )
        ? {
          value: user.academyId,
          label: getAcademyName(user.academyId),
        }
        : null,
    );
    setSelectedDept(
      department?.find((d) => d.departmentId === user.departmentId)
        ? {
          value: user.departmentId,
          label: getDepartmentName(user.departmentId),
        }
        : null,
    );
    setSelectedDiv(
      divisions?.find((d) => d.divisionId === user.divisionId)
        ? {
          value: user.divisionId,
          label: getDivisionName(user.divisionId),
        }
        : null,
    );
    setSelectedRole(
      roles?.find((r) => r.roleId === user.role?.roleId)
        ? {
          value: user.role?.roleId,
          label: user.role?.name,
        }
        : null,
    );
    setShowModal(true);
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!roleNameInput.trim()) return toast.error("Role name is required");
    try {
      await createRole({
        name: roleNameInput.trim(),
        description: roleDescriptionInput.trim(),
      }).unwrap();
      toast.success("Role created successfully");
      setShowRoleModal(false);
      setRoleNameInput("");
      setRoleDescriptionInput("");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create role");
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      await updateRole({
        id: editRoleId,
        name: roleNameInput.trim(),
        description: roleDescriptionInput.trim(),
      }).unwrap();
      toast.success("Role updated successfully");
      setShowRoleModal(false);
      setIsEditingRole(false);
      setEditRoleId(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update role");
    }
  };

  const handleDeleteRole = async (id) => {
    setDeleteRoleConfirm({ open: true, id });
  };

  const confirmDeleteRole = async () => {
    try {
      await deleteRole(deleteRoleConfirm.id).unwrap();
      toast.success("Role deleted");
      setDeleteRoleConfirm({ open: false, id: null });
    } catch (err) {
      toast.error("Failed to delete role");
    }
  };

  const confirmDeleteUser = async () => {
    try {
      await softDeleteUser(deleteUserConfirm.id);
      await refetchUsers();
      toast.success("User deleted");
      setDeleteUserConfirm({ open: false, id: null });
    } catch (err) {
      toast.error("Failed to delete user");
    }
  };

  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    if (!departmentName.trim())
      return toast.error("Department name is required");

    try {
      // Create department shared across all academies
      await createDepartment({
        name: departmentName.trim(),
      }).unwrap();

      // Wait a bit for the backend to process
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refetch to get updated data
      await refetchDepartments();

      setShowModalDepartment(false);
      setDepartmentName("");
      setSelectedAcademy(null);
      toast.success("Department created successfully");
    } catch (err) {
      console.error("Error creating department:", err);
      toast.error("Failed to create department");
    }
  };

  const handleEditDepartment = async (e) => {
    e.preventDefault();
    if (!editDepartmentId) {
      toast.error("Error: Department ID is missing");
      return;
    }
    try {
      // Find the original department to get its name
      const originalDept = department?.find(
        (d) => (d.departmentId || d._id) === editDepartmentId,
      );
      const originalName = originalDept?.name || originalDept?.departmentName;

      // Find all departments with the same name across all academies
      const depsToUpdate = department?.filter(
        (d) => (d.name || d.departmentName) === originalName,
      );

      // Update all matching departments with the new name
      const updatePromises = depsToUpdate?.map((d) =>
        updateDepartment({
          id: d.departmentId || d._id,
          name: departmentName.trim(),
          academyId: d.academyId, // Keep the same academy
        }).unwrap(),
      );

      await Promise.all(updatePromises);
      setShowModalDepartment(false);
      setIsEditing(false);
      setDepartmentName("");
      setSelectedAcademy(null);
      setEditDepartmentId(null);
      refetchDepartments();
      toast.success("Department updated for all academies");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleDeleteDepartment = async (id) => {
    if (!id) return;
    if (
      window.confirm(
        "Are you sure you want to delete this department from all academies?",
      )
    ) {
      try {
        // Find the department name from the clicked department
        const deptToDelete = department?.find(
          (d) => (d.departmentId || d._id) === id,
        );
        const deptName = deptToDelete?.name || deptToDelete?.departmentName;

        // Find all departments with the same name across all academies
        const depsToDelete = department?.filter(
          (d) => (d.name || d.departmentName) === deptName,
        );

        // Delete all matching departments
        const deletePromises = depsToDelete?.map((d) =>
          deleteDepartment(d.departmentId || d._id).unwrap(),
        );

        await Promise.all(deletePromises);
        refetchDepartments();
        toast.success("Department deleted from all academies");
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };

  // Division handlers
  const handleCreateDivision = async (e) => {
    e.preventDefault();
    if (!divisionName.trim() || !selectedDivisionDept) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await createDivision({
        name: divisionName.trim(),
        description: divisionDescription.trim(),
        departmentId: selectedDivisionDept.value,
      }).unwrap();
      setShowModalDivision(false);
      setDivisionName("");
      setDivisionDescription("");
      setSelectedDivisionDept(null);
      refetchDivisions();
      toast.success("Division created");
    } catch (err) {
      toast.error("Failed to create division");
    }
  };

  const handleEditDivision = async (e) => {
    e.preventDefault();
    if (!editDivisionId) {
      toast.error("Error: Division ID is missing");
      return;
    }
    try {
      await updateDivision({
        id: editDivisionId,
        name: divisionName.trim(),
        description: divisionDescription.trim(),
        departmentId: selectedDivisionDept.value,
      }).unwrap();
      setShowModalDivision(false);
      setIsEditingDivision(false);
      setDivisionName("");
      setDivisionDescription("");
      setSelectedDivisionDept(null);
      setEditDivisionId(null);
      refetchDivisions();
      toast.success("Division updated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleDeleteDivision = async (id) => {
    if (!id) return;
    if (window.confirm("Are you sure you want to delete this division?")) {
      try {
        await deleteDivision(id).unwrap();
        refetchDivisions();
        toast.success("Division deleted");
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };

  const handleAdminResetPassword = async (newPassword) => {
    try {
      await adminResetPassword({
        id: userToReset.userId,
        newPassword,
      }).unwrap();
      toast.success("Password has been reset successfully.");
      setShowResetModal(false);
      setUserToReset(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to reset password");
    }
  };

  const resetForm = () => {
    setName("");
    setEmployeeId("");
    setEmail("");
    setPassword("");
    setSelectedAcademy(null);
    setSelectedDept(null);
    setSelectedDiv(null);
    setSelectedRole(null);
    setEmailError("");
  };

  const filteredData = useMemo(() => {
    if (activeTab === "Department") {
      console.log("Raw department data:", department);

      // Get all departments that match the search term
      const allDepts = (department || []).filter((item) =>
        matchesSearchTerm(item, searchTerm),
      );

      console.log("After search filter:", allDepts);

      // Group by department name to show unique departments
      const uniqueDepts = [];
      const seenNames = new Set();

      for (const dept of allDepts) {
        const deptName = dept.name || dept.departmentName;
        console.log("Processing dept:", dept, "Name:", deptName);
        if (!seenNames.has(deptName)) {
          seenNames.add(deptName);
          uniqueDepts.push(dept);
        }
      }

      console.log("Unique departments:", uniqueDepts);
      return uniqueDepts;
    }
    if (activeTab === "Division") {
      return (divisions || []).filter(
        (item) =>
          matchesAcademyFilter(item, selectedAcademyId) &&
          matchesSearchTerm(item, searchTerm),
      );
    }
    if (activeTab === "Role") {
      return (roles || []).filter((item) =>
        matchesSearchTerm(item, searchTerm),
      );
    }
    return (data || []).filter((item) => {
      const roleName = item.role?.name?.toLowerCase();
      if (activeTab === "CFM") {
        if (!isCFMRole(roleName)) return false;
      } else if (roleName !== activeTab.toLowerCase()) {
        return false;
      }
      return (
        matchesAcademyFilter(item, selectedAcademyId) &&
        matchesSearchTerm(item, searchTerm)
      );
    });
  }, [
    activeTab,
    data,
    department,
    divisions,
    roles,
    selectedAcademyId,
    searchTerm,
    matchesAcademyFilter,
    matchesSearchTerm,
    isCFMRole,
  ]);

  const displayedData = filteredData.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE,
  );
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

  const getAcademyName = (id) =>
    academies?.find((a) => a.academyId === id || a._id === id)?.name || "N/A";
  const getDepartmentName = (id) =>
    department?.find((d) => d.departmentId === id)?.name || "N/A";
  const getDivisionName = (id) =>
    divisions?.find((d) => d.divisionId === id)?.name || "N/A";

  const renderTabButtons = () => (
    <div className="flex flex-wrap gap-1 border-b border-border">
      {TABS.map((tab) => (
        <button
          key={tab}
          onClick={() => {
            setActiveTab(tab);
            setCurrentPage(1);
          }}
          className={`relative px-4 py-2.5 text-sm font-medium transition-all rounded-t-lg ${activeTab === tab
            ? "bg-background text-foreground border-b-2 border-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
        >
          {tab}
          {activeTab === tab && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold tracking-tight">
            {activeTab} Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and organize {activeTab.toLowerCase()} data
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="border-b bg-card px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {activeTab !== "Role" && (
              <select
                value={selectedAcademyId}
                onChange={(e) => setSelectedAcademyId(e.target.value)}
                className="flex h-10 w-full sm:w-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All Academies</option>
                {academies?.map((a) => (
                  <option
                    key={a.academyId || a._id}
                    value={a.academyId || a._id}
                  >
                    {a.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex gap-2">
            {["Manager", "Supervisor", "Technician", "CFM"].includes(
              activeTab,
            ) && (
                <button
                  onClick={() => {
                    setIsEditingUser(false);
                    resetForm();
                    setShowModal(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add {activeTab}
                </button>
              )}
            {activeTab === "Department" && (
              <button
                onClick={() => {
                  setIsEditing(false);
                  setShowModalDepartment(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" /> Add Department
              </button>
            )}
            {activeTab === "Division" && (
              <button
                onClick={() => {
                  setIsEditingDivision(false);
                  setDivisionName("");
                  setDivisionDescription("");
                  setSelectedDivisionDept(null);
                  setShowModalDivision(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" /> Add Division
              </button>
            )}
            {activeTab === "Role" && (
              <button
                onClick={() => {
                  setIsEditingRole(false);
                  setRoleNameInput("");
                  setRoleDescriptionInput("");
                  setShowRoleModal(true);
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" /> Add Role
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="rounded-md border bg-card">
            <Table>
              <TableHeader>
                {activeTab === "Role" ? (
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold">Role Name</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="text-right font-semibold">
                      Actions
                    </TableHead>
                  </TableRow>
                ) : activeTab === "Department" ? (
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold">
                      Department Name
                    </TableHead>
                    {/* <TableHead className="font-semibold">Academy</TableHead> */}
                    <TableHead className="text-right font-semibold">
                      Actions
                    </TableHead>
                  </TableRow>
                ) : activeTab === "Division" ? (
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold">
                      Division Name
                    </TableHead>
                    <TableHead className="font-semibold">Department</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="text-right font-semibold">
                      Actions
                    </TableHead>
                  </TableRow>
                ) : (
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="font-semibold">User</TableHead>
                    <TableHead className="font-semibold">Employee ID</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Academy</TableHead>
                    <TableHead className="text-right font-semibold">
                      Actions
                    </TableHead>
                  </TableRow>
                )}
              </TableHeader>
              <TableBody>
                {displayedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-12 text-muted-foreground"
                    >
                      No {activeTab.toLowerCase()} found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedData.map((item, i) => (
                    <TableRow key={i}>
                      {activeTab === "Role" ? (
                        <>
                          <TableCell className="font-medium">
                            {item.name}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.description}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => {
                                  setEditRoleId(item.roleId);
                                  setRoleNameInput(item.name);
                                  setRoleDescriptionInput(item.description);
                                  setIsEditingRole(true);
                                  setShowRoleModal(true);
                                }}
                                className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteRole(item.roleId)}
                                className="inline-flex items-center justify-center rounded-md h-8 w-8 text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </TableCell>
                        </>
                      ) : activeTab === "Department" ? (
                        <>
                          <TableCell className="font-medium">
                            {item.name || item.departmentName}
                          </TableCell>
                          <TableCell className="text-right">
                            <button
                              onClick={() => {
                                setIsEditing(true);
                                const deptId = item.departmentId || item._id;
                                setEditDepartmentId(deptId);
                                setDepartmentName(
                                  item.name || item.departmentName,
                                );
                                setShowModalDepartment(true);
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const deptId = item.departmentId || item._id;
                                if (deptId) {
                                  handleDeleteDepartment(deptId);
                                }
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 text-destructive hover:bg-destructive/10 transition-colors ml-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </>
                      ) : activeTab === "Division" ? (
                        <>
                          <TableCell className="font-medium">
                            {item.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getDepartmentName(item.departmentId)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.description || "N/A"}
                          </TableCell>
                          <TableCell className="text-right">
                            <button
                              onClick={() => {
                                setIsEditingDivision(true);
                                const divId = item.divisionId || item._id;
                                setEditDivisionId(divId);
                                setDivisionName(item.name);
                                setDivisionDescription(item.description || "");
                                // Find the department object for the select field
                                if (department) {
                                  const dept = department.find(
                                    (d) =>
                                      d.departmentId === item.departmentId ||
                                      d._id === item.departmentId,
                                  );
                                  if (dept) {
                                    setSelectedDivisionDept({
                                      value: dept.departmentId || dept._id,
                                      label: dept.name,
                                    });
                                  }
                                }
                                setShowModalDivision(true);
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                const divId = item.divisionId || item._id;
                                if (divId) {
                                  handleDeleteDivision(divId);
                                }
                              }}
                              className="inline-flex items-center justify-center rounded-md h-8 w-8 text-destructive hover:bg-destructive/10 transition-colors ml-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <div
                              className="flex items-center gap-3 cursor-pointer group"
                              onClick={() =>
                                navigate(`/admin/users/${item.email}`)
                              }
                            >
                              <Avatar src={item.image} size={40} />
                              <div>
                                <div className="font-medium group-hover:text-primary transition-colors">
                                  {item.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {item.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.employeeId}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {item.email}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getAcademyName(item.academyId)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditUserModal(item);
                                }}
                                className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-primary transition-colors"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUserToReset(item);
                                  setShowResetModal(true);
                                }}
                                title="Reset Password"
                                className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-accent text-blue-600 transition-colors"
                              >
                                <Lock className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteUserConfirm({
                                    open: true,
                                    id: item.userId,
                                  });
                                }}
                                className="inline-flex items-center justify-center rounded-md h-8 w-8 hover:bg-destructive/10 text-destructive transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center rounded-md h-9 px-4 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center rounded-md h-9 px-4 text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:pointer-events-none"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Modal - shadcn Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditingUser ? "Update Profile" : `Add ${activeTab}`}
            </DialogTitle>
            <DialogDescription>
              {isEditingUser
                ? "Update user information and role assignments"
                : `Create a new ${activeTab.toLowerCase()} account`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeId">Employee ID</Label>
              <Input
                id="employeeId"
                placeholder="e.g., NHDCL@2023"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@nhdcl.bt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}
            </div>

            {!isEditingUser && (
              <div className="space-y-2">
                <Label htmlFor="password">Initial Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter initial password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Academy</Label>
              <Select
                value={selectedAcademy?.value || ""}
                onValueChange={(val) => {
                  const selected = academies?.find(
                    (a) => (a.academyId || a._id) === val,
                  );
                  setSelectedAcademy({
                    value: val,
                    label: selected?.name,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Academy" />
                </SelectTrigger>
                <SelectContent>
                  {academies?.map((a) => (
                    <SelectItem
                      key={a.academyId || a._id}
                      value={a.academyId || a._id}
                    >
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Only show Department and Division for roles that need them */}
            {activeTab !== "CFM" && activeTab !== "admin" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Department
                    {((activeTab || "").toLowerCase() === "manager" ||
                      (activeTab || "").toLowerCase() === "supervisor" ||
                      (activeTab || "").toLowerCase() === "technician") && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                  </Label>
                  <Select
                    value={selectedDept?.value || ""}
                    onValueChange={(val) => {
                      const selected = department?.find(
                        (d) => (d.departmentId || d._id) === val,
                      );
                      setSelectedDept({
                        value: val,
                        label: selected?.departmentName || selected?.name,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Dept" />
                    </SelectTrigger>
                    <SelectContent>
                      {department?.map((d) => (
                        <SelectItem key={d.departmentId || d._id} value={d.departmentId || d._id}>
                          {d.departmentName || d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    Division
                    {((activeTab || "").toLowerCase() === "supervisor" ||
                      (activeTab || "").toLowerCase() === "technician") && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                  </Label>
                  <Select
                    value={selectedDiv?.value || ""}
                    onValueChange={(val) => {
                      const selected = divisions?.find(
                        (div) => (div.divisionId || div._id) === val,
                      );
                      setSelectedDiv({
                        value: val,
                        label: selected?.name,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Division" />
                    </SelectTrigger>
                    <SelectContent>
                      {(() => {
                        console.log("All divisions:", divisions);
                        console.log("Selected department:", selectedDept);
                        const filtered = divisions?.filter(
                          (div) => (div.departmentId || div._id) === selectedDept?.value,
                        );
                        console.log("Filtered divisions:", filtered);
                        return filtered?.map((div) => (
                          <SelectItem
                            key={div.divisionId || div._id}
                            value={div.divisionId || div._id}
                          >
                            {div.name}
                          </SelectItem>
                        ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {isEditingUser && (
              <div className="space-y-2">
                <Label>Role</Label>
                <Select
                  value={selectedRole?.value || ""}
                  onValueChange={(val) => {
                    const selected = roles?.find((r) => r.roleId === val);
                    setSelectedRole({
                      value: val,
                      label: selected?.name,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles?.map((r) => (
                      <SelectItem key={r.roleId} value={r.roleId}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={isEditingUser ? handleUpdateUser : handleCreateUser}
              disabled={isCreatingUser || isUpdatingUser}
            >
              {isCreatingUser || isUpdatingUser
                ? "Processing..."
                : isEditingUser
                  ? "Update User"
                  : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Modal - shadcn Dialog */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditingRole ? "Edit Role Definition" : "Add New Role"}
            </DialogTitle>
            <DialogDescription>
              {isEditingRole
                ? "Update role name and description"
                : "Create a new role definition for the system"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={isEditingRole ? handleUpdateRole : handleCreateRole}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="roleName">Role Name</Label>
                <Input
                  id="roleName"
                  placeholder="e.g., Manager, Supervisor"
                  value={roleNameInput}
                  onChange={(e) => setRoleNameInput(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="roleDescription">Description</Label>
                <textarea
                  id="roleDescription"
                  placeholder="Describe the role responsibilities..."
                  value={roleDescriptionInput}
                  onChange={(e) => setRoleDescriptionInput(e.target.value)}
                  rows={3}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {isEditingRole ? "Update Role" : "Create Role"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Department Modal - shadcn Dialog */}
      <Dialog open={showModalDepartment} onOpenChange={setShowModalDepartment}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditing ? "Edit Department" : "Add Department"}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Update department name for all academies"
                : "Create a new department for all academies"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="departmentName">Department Name</Label>
              <Input
                id="departmentName"
                placeholder="e.g., Maintenance, Operations"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
              />
            </div>

            <div className="rounded-md bg-muted p-3">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong>{" "}
                {isEditing
                  ? "This will update the department name for all academies."
                  : "This department will be created for all academies automatically."}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModalDepartment(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={
                isEditing ? handleEditDepartment : handleCreateDepartment
              }
            >
              {isEditing ? "Update Department" : "Create Department"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Division Modal - shadcn Dialog */}
      <Dialog open={showModalDivision} onOpenChange={setShowModalDivision}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {isEditingDivision ? "Edit Division" : "Add Division"}
            </DialogTitle>
            <DialogDescription>
              {isEditingDivision
                ? "Update division information"
                : "Create a new division and assign it to a department"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="divisionName">Division Name</Label>
              <Input
                id="divisionName"
                placeholder="e.g., Engineering, Operations"
                value={divisionName}
                onChange={(e) => setDivisionName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="divisionDescription">Description</Label>
              <Input
                id="divisionDescription"
                placeholder="Division description (optional)"
                value={divisionDescription}
                onChange={(e) => setDivisionDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                value={selectedDivisionDept?.value || ""}
                onValueChange={(val) => {
                  const selected = department?.find(
                    (d) => (d.departmentId || d._id) === val,
                  );
                  setSelectedDivisionDept({
                    value: val,
                    label: selected?.name || selected?.departmentName,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Department" />
                </SelectTrigger>
                <SelectContent>
                  {department?.map((d) => (
                    <SelectItem
                      key={d.departmentId || d._id}
                      value={d.departmentId || d._id}
                    >
                      {d.name || d.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModalDivision(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={
                isEditingDivision ? handleEditDivision : handleCreateDivision
              }
              disabled={isUpdatingDivision}
            >
              {isUpdatingDivision
                ? "Processing..."
                : isEditingDivision
                  ? "Update Division"
                  : "Create Division"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Confirmation Dialog */}
      <AlertDialog
        open={deleteRoleConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteRoleConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Role Definition?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this role definition? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteRoleConfirm({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRole}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog
        open={deleteUserConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteUserConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteUserConfirm({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ResetPasswordModal
        show={showResetModal}
        userName={userToReset?.name}
        onClose={() => {
          setShowResetModal(false);
          setUserToReset(null);
        }}
        onReset={handleAdminResetPassword}
        isResetting={isResettingPassword}
      />
    </div>
  );
};

export default AdminUser;
